import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { LegalBasis, LegalBasisDraft } from './legal-basis';
import { IdObject } from 'src/app/core/id-object';

@Injectable({
  providedIn: 'root',
})
export class LegalBasisService {
  private static listUrl = (apiUrl: string): string => `${apiUrl}legalBasis`;
  private static singleUrl = (apiUrl: string, id: string): string =>
    `${apiUrl}legalBasis/${id}`;
  private static searchUrl = (apiUrl: string): string =>
    `${apiUrl}legalBasis/search`;

  constructor(private httpClient: HttpClient) {}

  public getList(): Observable<LegalBasis[]> {
    const { apiUrl } = environment;
    const url = LegalBasisService.listUrl(apiUrl);

    return this.httpClient
      .get<LegalBasis[]>(url)
      .pipe(
        catchError(
          this.handleError<LegalBasis[]>('LegalBasisService->getList', []),
        ),
      );
  }

  public getSingle(id: string): Observable<LegalBasis> {
    const { apiUrl } = environment;
    const url = LegalBasisService.singleUrl(apiUrl, id);

    return this.httpClient
      .get<LegalBasis>(url)
      .pipe(
        catchError(
          this.handleError<LegalBasis>('LegalBasisService->getSingle'),
        ),
      );
  }

  public create(legalBasisDraft: LegalBasisDraft): Observable<string> {
    const { apiUrl } = environment;
    const url = LegalBasisService.listUrl(apiUrl);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.httpClient
      .post<IdObject>(url, legalBasisDraft, { headers })
      .pipe(
        map((idObject: IdObject) => idObject.id),
        catchError(this.handleError<string>('LegalBasisService->create')),
      );
  }

  public patch(
    id: string,
    legalBasisDraft: LegalBasisDraft,
  ): Observable<boolean> {
    const { apiUrl } = environment;
    const url = LegalBasisService.singleUrl(apiUrl, id);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.httpClient
      .patch<boolean>(url, legalBasisDraft, { headers })
      .pipe(
        catchError(
          this.handleError<boolean>('LegalBasisService->patch', false),
        ),
      );
  }

  public remove(id: string): Observable<boolean> {
    const { apiUrl } = environment;
    const url = LegalBasisService.singleUrl(apiUrl, id);

    return this.httpClient.delete<void>(url).pipe(
      map(() => true),
      catchError(this.handleError('LegalBasisService->remove', false)),
    );
  }

  public search(searchData: any): Observable<LegalBasis[]> {
    const { apiUrl } = environment;
    const url = LegalBasisService.searchUrl(apiUrl);
    const headers = new HttpHeaders({ 'Content-Type': 'application/json' });

    return this.httpClient
      .post<LegalBasis[]>(url, searchData, { headers })
      .pipe(
        catchError(
          this.handleError<LegalBasis[]>('LegalBasisService->search', []),
        ),
      );
  }

  /**
   * Handle Http operation that failed.
   * Let the app continue.
   * @param operation - name of the operation that failed
   * @param result - optional value to return as the observable result
   */
  private handleError<T>(
    operation = 'operation',
    result?: T,
  ): (error: any) => Observable<T> {
    return (error: any): Observable<T> => {
      console.error(`${operation} failed: `, error);

      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  }
}
